/**
 * @tyndo/types — Canonical NCR disposition vocabulary.
 *
 * A non-conformance / CAPA disposition is the terminal outcome recorded against
 * a defective lot or record. Three UIs used to persist DIFFERENT string values
 * for the SAME outcome — `quality-ui NonConformanceForm` wrote `"Use As Is"`
 * while `quality-ui NcrCapaWorkbench` wrote `"UseAsIs"` — so records were not
 * co-queryable. This module is the single source of truth: every UI persists
 * the canonical machine value (PascalCase, matching the backend disposition
 * decision terms) and renders the shared human label.
 *
 * DO NOT persist a display label. Persist a {@link NcrDisposition} value.
 */

/** Canonical machine values for an NCR / CAPA disposition (what gets persisted). */
export const NCR_DISPOSITIONS = [
  'UseAsIs',
  'Rework',
  'Scrap',
  'Return',
  'UseLimited',
] as const;

export type NcrDisposition = (typeof NCR_DISPOSITIONS)[number];

/** Human-readable label for each canonical disposition (what gets rendered). */
export const NCR_DISPOSITION_LABELS: Record<NcrDisposition, string> = {
  UseAsIs: 'Use as is',
  Rework: 'Rework',
  Scrap: 'Scrap',
  Return: 'Return',
  UseLimited: 'Use limited',
};

/** Ordered `{ value, label }` options for a disposition `<Select>`. */
export const NCR_DISPOSITION_OPTIONS: ReadonlyArray<{
  value: NcrDisposition;
  label: string;
}> = NCR_DISPOSITIONS.map((value) => ({
  value,
  label: NCR_DISPOSITION_LABELS[value],
}));

/** Type guard: is `value` a canonical NCR disposition? */
export function isNcrDisposition(value: unknown): value is NcrDisposition {
  return (
    typeof value === 'string' &&
    (NCR_DISPOSITIONS as readonly string[]).includes(value)
  );
}

/**
 * Best-effort migration of a legacy / free-text disposition string to its
 * canonical value. Handles the historical drift (`"Use As Is"`, `"use-as-is"`,
 * spacing / casing variants). Returns `undefined` when no canonical match
 * exists so callers can surface the raw value rather than silently coercing.
 */
export function canonicalizeNcrDisposition(
  raw: string | null | undefined,
): NcrDisposition | undefined {
  if (!raw) return undefined;
  const normalized = raw.replace(/[\s_-]+/g, '').toLowerCase();
  return NCR_DISPOSITIONS.find((d) => d.toLowerCase() === normalized);
}
